import React from 'react';
import { cn } from '../../utils/cn';

interface CountBadgeProps {
  /** The number to show. Nothing is drawn at zero, below zero, or for a non-number. */
  count: number;
  /** Highest number shown as is; anything above renders as `${max}+`. */
  max?: number;
  /** The icon the count sits on. */
  children: React.ReactNode;
  /** Extra classes for the wrapper. */
  className?: string;
  /** Test id for the bubble itself (the trigger keeps its own). */
  bubbleTestId?: string;
}

/**
 * An icon with a count bubble on its inline-end top corner (CO-873). The one
 * bubble for the top bar: the notifications bell and Pending Actions both use
 * it, so the two cannot drift apart again.
 *
 * - The wrapper is the positioned box: 13px of padding around a 20px icon is
 *   the 46px box the bell has always had. Do not also pad the trigger.
 * - Spans only: both call sites render this inside a <button>.
 * - The bubble is aria-hidden. The trigger's accessible name carries the count
 *   where a count is announced.
 * - `text-[12px]`, not `text-xs`: the app restates `.text-xs` with its own
 *   line-height (assets/styles/utilities.css).
 */
const CountBadge: React.FC<CountBadgeProps> = ({ count, max = 99, children, className, bubbleTestId }) => {
  const visible = Number.isFinite(count) && count > 0;

  return (
    <span className={cn('relative inline-flex p-[13px]', className)}>
      {children}
      {visible && (
        <span
          aria-hidden="true"
          data-testid={bubbleTestId}
          className="pointer-events-none absolute top-0 end-0 z-10 rounded-[10px] bg-destructive px-[5px] text-[12px] font-normal leading-[18px] tabular-nums text-destructive-foreground"
        >
          {count > max ? `${max}+` : count}
        </span>
      )}
    </span>
  );
};

export default CountBadge;
